import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import { FormAccordion } from '@/components/contracts/ContractFormAccordion'
import {
  ContractFormFooter,
  ContractFormHeader,
  ContractInformationSection,
  FinalizationSection,
  PlanningSection,
  ServiceInformationSection,
  addMilestone,
  addMonthsMinusDay,
  BILLING_FREQUENCIES,
  durationLabel,
  PLANNING_CARD_FREQUENCIES,
  TIME_SLOTS,
  type BringItem,
  type Milestone,
} from '@/components/contracts/form'
import type { PendingAttachFilesHandle } from '@/components/shared'
import { toast } from '@/components/ui/toast'
import { TerminateContractDialog } from '@/components/contracts/TerminateContractDialog'
import {
  useContract,
  useCreateContract,
  useUpdateContract,
} from '@/hooks/useContracts'
import { todayIsoDate } from '@/lib/date'
import { isContractEditable } from '@/lib/contracts'
import { formatCurrency, parseCurrencyInput } from '@/lib/format'
import { getApiErrorMessage } from '@/services/api'
import { clientsApi } from '@/services/clients'
import { contractsApi } from '@/services/contracts'
import type {
  ContractStatus,
  ContractTimeSlot,
  ContractType,
  CreateContractRequest,
  PlanningFrequency,
  Weekday,
} from '@/types'

interface Props {
  mode: 'create' | 'edit'
}

export default function ContractFormPage({ mode }: Props) {
  const { t } = useTranslation('contracts')
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const preselectClient = searchParams.get('client_id') ?? ''
  const quoteId = searchParams.get('quote_id') ?? ''
  const editing = mode === 'edit'

  const { data: detail, isLoading } = useContract(editing ? id : undefined)
  const create = useCreateContract()
  const update = useUpdateContract(id ?? '')

  const [clientId, setClientId] = useState(preselectClient)
  const [clientLabel, setClientLabel] = useState('')
  const [title, setTitle] = useState('')
  const [contractType, setContractType] = useState<ContractType | ''>('')
  const [notes, setNotes] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [frequency, setFrequency] = useState('')
  const [amountExcl, setAmountExcl] = useState('')
  const [vatRate, setVatRate] = useState('20')
  const [items, setItems] = useState<BringItem[]>([])
  const [checklist, setChecklist] = useState<string[]>([])
  const [autoRenew, setAutoRenew] = useState(false)
  const [requireSignature, setRequireSignature] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)
  const [terminateOpen, setTerminateOpen] = useState(false)
  const pendingAttachments = useRef<PendingAttachFilesHandle>(null)

  const { data: selectedClient } = useQuery({
    queryKey: ['clients', 'detail', clientId],
    queryFn: () => clientsApi.get(clientId),
    enabled: !editing && !!clientId,
  })

  const [categoryIds, setCategoryIds] = useState<string[]>([])
  const [categoryOptions, setCategoryOptions] = useState<
    { id: string; name: string }[]
  >([])

  const [interventionsPerYear, setInterventionsPerYear] = useState('')
  const [planningFrequency, setPlanningFrequency] = useState<
    PlanningFrequency | ''
  >('')
  const [preferredDays, setPreferredDays] = useState<Weekday[]>([])
  const [selectedSlots, setSelectedSlots] = useState<number[]>([])
  const [customSlot, setCustomSlot] = useState<ContractTimeSlot | null>(null)
  const [milestone, setMilestone] = useState<Milestone>('1y')

  useEffect(() => {
    let active = true
    contractsApi
      .serviceCategoryOptions()
      .then(res => {
        if (active) setCategoryOptions(res.data ?? [])
      })
      .catch(() => {
        /* leave empty on failure */
      })
    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    if (editing && detail?.data) {
      const c = detail.data
      /* eslint-disable react-hooks/set-state-in-effect */
      setClientId(c.client_id)
      setClientLabel(c.client?.name ?? '')
      setTitle(c.title ?? '')
      setContractType(c.contract_type ?? '')
      setNotes(c.notes ?? '')
      setStartDate(c.start_date ?? '')
      setEndDate(c.end_date ?? '')
      setFrequency(c.frequency ?? '')
      setAmountExcl(c.amount != null ? String(c.amount) : '')
      setVatRate(c.vat_rate != null ? String(c.vat_rate) : '20')
      setAutoRenew(c.auto_renew)
      setRequireSignature(c.require_signature ?? false)
      setCategoryIds(c.service_category_ids ?? [])
      setItems(
        (c.items_to_bring ?? []).map(it => ({
          service_item_id: it.service_item_id ?? null,
          name: it.name,
          quantity: String(it.quantity ?? 1),
        }))
      )
      setInterventionsPerYear(
        c.interventions_per_year != null ? String(c.interventions_per_year) : ''
      )
      setPlanningFrequency(c.planning_frequency ?? '')
      setPreferredDays(c.preferred_days ?? [])
      setChecklist(c.intervention_resume ?? [])
      const persisted = c.preferred_time_slots ?? []
      const fixed: number[] = []
      let custom: ContractTimeSlot | null = null
      persisted.forEach(slot => {
        const idx = TIME_SLOTS.findIndex(
          s => s.start === slot.start && s.end === slot.end
        )
        if (idx >= 0) fixed.push(idx)
        else custom = slot
      })
      setSelectedSlots(fixed)
      setCustomSlot(custom)
      /* eslint-enable react-hooks/set-state-in-effect */
    }
  }, [editing, detail])

  const excl = parseCurrencyInput(amountExcl) ?? 0
  const vat = Number(vatRate) || 0
  const incl = useMemo(() => excl * (1 + vat / 100), [excl, vat])
  const duration = durationLabel(startDate, endDate, t)
  const invoicesPerYear = BILLING_FREQUENCIES.find(
    f => f.value === frequency
  )?.invoices
  const byDueDate = invoicesPerYear
    ? formatCurrency(incl / invoicesPerYear, 'EUR', 2)
    : t('form.finance.dependOnFrequency')

  const applyQuickDuration = (months: number) => {
    const start = startDate || todayIsoDate()
    if (!startDate) setStartDate(start)
    setEndDate(addMonthsMinusDay(start, months))
    setErrors(e => ({ ...e, start_date: '', end_date: '' }))
  }

  const renewalNewStart = useMemo(() => {
    if (!endDate) return null
    const d = new Date(endDate)
    d.setDate(d.getDate() + 1)
    return d
  }, [endDate])
  const renewalNewEnd = useMemo(() => {
    if (!renewalNewStart) return null
    return addMilestone(renewalNewStart, milestone)
  }, [milestone, renewalNewStart])

  const validate = () => {
    const next: Record<string, string> = {}
    const requiredMessage = t('form.validation.required')
    if (!clientId) next.client_id = t('form.validation.client')
    if (!contractType) next.contract_type = t('form.validation.contractType')
    if (!startDate) next.start_date = t('form.validation.startDate')
    if (!endDate) next.end_date = t('form.validation.endDate')
    if (!frequency) next.frequency = t('form.validation.frequency')
    if (!amountExcl || excl <= 0) next.amount = t('form.validation.amount')
    if (startDate && endDate && new Date(endDate) <= new Date(startDate))
      next.end_date = t('form.validation.endAfterStart')
    if (categoryIds.length === 0) next.service_category_ids = requiredMessage
    if (!PLANNING_CARD_FREQUENCIES.some(o => o.value === planningFrequency))
      next.planning_frequency = requiredMessage
    if (!interventionsPerYear || Number(interventionsPerYear) <= 0)
      next.interventions_per_year = requiredMessage
    if (preferredDays.length === 0) next.preferred_days = requiredMessage
    if (collectTimeSlots().length === 0)
      next.preferred_time_slots = requiredMessage
    if (customSlot && !customSlot.start)
      next.custom_slot_start = requiredMessage
    if (customSlot && !customSlot.end) next.custom_slot_end = requiredMessage
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const collectTimeSlots = (): ContractTimeSlot[] => {
    const slots = selectedSlots.map(i => TIME_SLOTS[i])
    if (customSlot && customSlot.start && customSlot.end) slots.push(customSlot)
    return slots
  }

  const buildBody = (): CreateContractRequest => ({
    client_id: clientId,
    title: title.trim() || null,
    quote_id: quoteId || undefined,
    contract_type: contractType || undefined,
    start_date: startDate || undefined,
    end_date: endDate || undefined,
    frequency: frequency || undefined,
    amount: excl || undefined,
    vat_rate: vat,
    auto_renew: autoRenew,
    notes: notes.trim() || undefined,
    require_signature: requireSignature,
    service_category_ids: categoryIds,
    items_to_bring: items
      .filter(it => it.name.trim() !== '')
      .map(it => ({
        service_item_id: it.service_item_id,
        name: it.name.trim(),
        quantity: Number(it.quantity) || 1,
      })),
    interventions_per_year: Number(interventionsPerYear),
    planning_frequency: planningFrequency as PlanningFrequency,
    preferred_days: preferredDays,
    preferred_time_slots: collectTimeSlots(),
    intervention_resume: checklist.map(s => s.trim()).filter(Boolean),
  })

  const save = async (andSend: boolean) => {
    if (!validate()) return
    setSaving(true)
    try {
      let contractId = id
      if (editing && id) {
        await update.mutateAsync(buildBody())
        if (pendingAttachments.current?.count()) {
          try {
            await pendingAttachments.current.flush(id)
          } catch {
            toast({
              variant: 'destructive',
              title: t('detail.attachments.error'),
            })
          }
        }
        toast({ variant: 'success', title: t('form.toast.updated') })
      } else {
        const res = await create.mutateAsync(buildBody())
        contractId = res.data?.id
        if (contractId && pendingAttachments.current?.count()) {
          try {
            await pendingAttachments.current.flush(contractId)
          } catch {
            toast({
              variant: 'destructive',
              title: t('detail.attachments.error'),
            })
          }
        }
        toast({ variant: 'success', title: t('form.toast.saved') })
      }
      if (andSend && contractId) {
        navigate(`/contracts/${contractId}?send=1`)
        return
      }
      if (contractId) navigate(`/contracts/${contractId}`)
      else navigate('/contracts')
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(
          e,
          editing ? t('form.toast.updateError') : t('form.toast.saveError')
        ),
      })
    } finally {
      setSaving(false)
    }
  }

  if (editing && isLoading) {
    return (
      <div className='py-10 text-center text-muted-foreground'>
        {t('form.loading')}
      </div>
    )
  }

  if (editing && detail?.data && !isContractEditable(detail.data.status)) {
    return <Navigate to={`/contracts/${detail.data.id}`} replace />
  }

  const formStatus: ContractStatus =
    editing && detail?.data?.status ? detail.data.status : 'draft'
  const incompleteContractFields = [
    !clientId,
    !contractType,
    !startDate,
    !endDate,
    !frequency,
    !amountExcl || excl <= 0,
  ].filter(Boolean).length
  const incompleteServiceFields = categoryIds.length === 0 ? 1 : 0
  const hasTimeSlot =
    selectedSlots.length > 0 || (!!customSlot?.start && !!customSlot?.end)
  const incompletePlanningFields = [
    !PLANNING_CARD_FREQUENCIES.some(o => o.value === planningFrequency),
    !interventionsPerYear || Number(interventionsPerYear) <= 0,
    preferredDays.length === 0,
    !hasTimeSlot,
  ].filter(Boolean).length

  const endDateInvalid =
    !!startDate && !!endDate && new Date(endDate) <= new Date(startDate)
  const shownErrors = {
    client_id: clientId ? '' : errors.client_id,
    title: title.trim() ? '' : errors.title,
    contract_type: contractType ? '' : errors.contract_type,
    start_date: startDate ? '' : errors.start_date,
    end_date: !endDate
      ? errors.end_date
      : endDateInvalid
        ? errors.end_date || t('form.validation.endAfterStart')
        : '',
    frequency: frequency ? '' : errors.frequency,
    amount: amountExcl && excl > 0 ? '' : errors.amount,
    service_category_ids:
      categoryIds.length > 0 ? '' : errors.service_category_ids,
    planning_frequency: PLANNING_CARD_FREQUENCIES.some(
      o => o.value === planningFrequency
    )
      ? ''
      : errors.planning_frequency,
    interventions_per_year:
      interventionsPerYear && Number(interventionsPerYear) > 0
        ? ''
        : errors.interventions_per_year,
    preferred_days: preferredDays.length > 0 ? '' : errors.preferred_days,
    preferred_time_slots: hasTimeSlot ? '' : errors.preferred_time_slots,
    custom_slot_start:
      !customSlot || customSlot.start ? '' : errors.custom_slot_start,
    custom_slot_end:
      !customSlot || customSlot.end ? '' : errors.custom_slot_end,
  }

  const clearError = (field: string) =>
    setErrors(current => ({ ...current, [field]: '' }))

  return (
    <div className='flex min-h-0 flex-1 flex-col'>
      <ContractFormHeader
        formStatus={formStatus}
        editing={editing}
        saving={saving}
        contractId={editing ? id : undefined}
        contractNumber={detail?.data?.number}
        onCancel={() => navigate(-1)}
        onSaveDraft={() => save(false)}
        onSaveAndSend={() => save(true)}
        onTerminate={
          editing && detail?.data ? () => setTerminateOpen(true) : undefined
        }
      />

      <div className='max-w-[1092px] w-full mx-auto flex min-h-0 flex-1 flex-col px-16 py-6'>
        <FormAccordion
          type='single'
          defaultValue='contract-information'
          className='flex flex-col gap-6'
        >
          <ContractInformationSection
            incompleteFields={incompleteContractFields}
            startDate={startDate}
            endDate={endDate}
            amountIncl={incl}
            shownErrors={shownErrors}
            setErrors={setErrors}
            clientId={clientId}
            clientLabel={clientLabel}
            selectedClientName={selectedClient?.data?.name}
            selectedClientCategory={
              selectedClient?.data?.category ?? detail?.data?.client?.category
            }
            setClientId={setClientId}
            setClientLabel={setClientLabel}
            title={title}
            setTitle={setTitle}
            contractType={contractType}
            setContractType={setContractType}
            notes={notes}
            setNotes={setNotes}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
            duration={duration}
            applyQuickDuration={applyQuickDuration}
            frequency={frequency}
            setFrequency={setFrequency}
            amountExcl={amountExcl}
            setAmountExcl={setAmountExcl}
            vatRate={vatRate}
            setVatRate={setVatRate}
            byDueDate={byDueDate}
          />
          <ServiceInformationSection
            incompleteFields={incompleteServiceFields}
            categoryError={shownErrors.service_category_ids}
            categoryIds={categoryIds}
            setCategoryIds={setCategoryIds}
            clearCategoryError={() => clearError('service_category_ids')}
            categoryOptions={categoryOptions}
            items={items}
            setItems={setItems}
            checklist={checklist}
            setChecklist={setChecklist}
          />
          <PlanningSection
            incompleteFields={incompletePlanningFields}
            errors={shownErrors}
            clearError={clearError}
            planningFrequency={planningFrequency}
            setPlanningFrequency={setPlanningFrequency}
            interventionsPerYear={interventionsPerYear}
            setInterventionsPerYear={setInterventionsPerYear}
            preferredDays={preferredDays}
            setPreferredDays={setPreferredDays}
            selectedSlots={selectedSlots}
            setSelectedSlots={setSelectedSlots}
            customSlot={customSlot}
            setCustomSlot={setCustomSlot}
          />
          <FinalizationSection
            autoRenew={autoRenew}
            setAutoRenew={setAutoRenew}
            milestone={milestone}
            setMilestone={setMilestone}
            renewalNewStart={renewalNewStart}
            renewalNewEnd={renewalNewEnd}
            requireSignature={requireSignature}
            setRequireSignature={setRequireSignature}
            editing={editing}
            contractId={id}
            clientId={clientId}
            pendingAttachments={pendingAttachments}
          />
        </FormAccordion>
      </div>

      <ContractFormFooter
        amountIncl={incl}
        saving={saving}
        onSaveAndSend={() => save(true)}
      />

      {editing && detail?.data && (
        <TerminateContractDialog
          key={terminateOpen ? detail.data.id : 'closed'}
          contract={terminateOpen ? detail.data : null}
          onOpenChange={setTerminateOpen}
        />
      )}
    </div>
  )
}
